import { loadGoogleMapsLibrary } from "./useGooglePlacesSuggestions";

export interface PickedLocation {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

export interface MapPickerHandle {
  destroy: () => void;
}

let mapsLibPromise: Promise<any> | null = null;
let geocodingLibPromise: Promise<any> | null = null;

const loadMapsLibrary = (): Promise<any> => {
  if (!mapsLibPromise) mapsLibPromise = loadGoogleMapsLibrary("maps");
  return mapsLibPromise;
};

const loadGeocodingLibrary = (): Promise<any> => {
  if (!geocodingLibPromise) geocodingLibPromise = loadGoogleMapsLibrary("geocoding");
  return geocodingLibPromise;
};

/**
 * Mounts an interactive Google Map into `container`. Clicking the map drops
 * (or moves) a marker, reverse-geocodes the point, and reports it via `onPick`.
 */
export const mountLocationPicker = async (
  container: HTMLDivElement,
  center: { lat: number; lng: number },
  marker: { lat: number; lng: number } | null,
  onPick: (location: PickedLocation) => void,
  onLoadingChange: (loading: boolean) => void,
): Promise<MapPickerHandle> => {
  const [mapsLib, geocodingLib] = await Promise.all([loadMapsLibrary(), loadGeocodingLibrary()]);

  const map = new mapsLib.Map(container, {
    center,
    zoom: marker ? 14 : 12,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
  });
  const geocoder = new geocodingLib.Geocoder();

  let markerInstance: any = marker
    ? new window.google.maps.Marker({ map, position: marker })
    : null;

  const listener = map.addListener("click", async (e: any) => {
    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();
    if (lat == null || lng == null) return;

    if (markerInstance) markerInstance.setPosition({ lat, lng });
    else markerInstance = new window.google.maps.Marker({ map, position: { lat, lng } });

    onLoadingChange(true);
    try {
      const response = await geocoder.geocode({ location: { lat, lng } });
      const result = response?.results?.[0];
      const address = result?.formatted_address ?? "";
      const name = address.split(",")[0]?.trim() || address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      onPick({ name, address, latitude: lat, longitude: lng });
    } catch {
      onPick({ name: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, address: "", latitude: lat, longitude: lng });
    } finally {
      onLoadingChange(false);
    }
  });

  return {
    destroy: () => {
      window.google.maps.event.removeListener(listener);
      if (markerInstance) markerInstance.setMap(null);
    },
  };
};

/** Mounts a read-only Google Map into `container`, centered on `position` with a marker. */
export const mountLocationViewer = async (
  container: HTMLDivElement,
  position: { lat: number; lng: number },
): Promise<MapPickerHandle> => {
  const mapsLib = await loadMapsLibrary();

  const map = new mapsLib.Map(container, {
    center: position,
    zoom: 14,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
  });
  const marker = new window.google.maps.Marker({ map, position });

  return {
    destroy: () => {
      marker.setMap(null);
    },
  };
};
